import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Offcanvas, Row } from "react-bootstrap";
import { Moment } from "moment";

import Tooltip from "src/components/tooltip";
import InfiniteScroll from "src/components/infiniteScroll";
import DownloadReports from "../downloadReports";

import { intializeDownloadReport, invalidateReportsDownloadData } from "src/redux/actions/reports";

import _ from "lodash";
import cx from "classnames";
import styles from "./reportListView.module.scss";
import userRoleService from "src/utils/userRoles.service";

import { PropertyType } from "src/types/clusterFilters";
import { EntitiesFilterTooltip } from "../entitiesFilterTooltip";
import { AdvocacyTables, DiscoveryTables, EngagementTables, PerformanceTables, ReportTabs } from "src/types/reports";

export interface Filter {
  property: {
    type: PropertyType;
  };
  operator: string;
  value: any[];
}

interface reportListViewProps {
  title: string;
  reportDate?: ReportDate;
  description?: string;
  items: any;
  renderItem: any;
  canDownload?: boolean;
  downloadConfig?: {
    name: string;
    schema: any;
    fileName: string;
    title: string;
    reportType: string;
  };
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  renderListItem?: any;
  columns: string[];
  actionHandler?: () => void;
  firstColumn?: string;
  viewMore?: () => void;
  isSearchGrid?: boolean;
  isSearchQueries?: boolean;
  filters: Filter[];
tabName?: ReportTabs;
  tableName?: DiscoveryTables | EngagementTables | PerformanceTables | AdvocacyTables;
}

interface DownloadConfig {
  schema: any[];
  fileName: string;
  title: string;
  reportType: string;
  actionId?: string;
  data?: any;
  pagination?: any;
  fetchState?: any;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}
interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
}

const ReportListView: React.FC<reportListViewProps> = ({
  title,
  items,
  description,
  renderItem,
  canDownload,
  downloadConfig,
  loading,
  pagination,
  loadMore,
  renderListItem,
  columns,
  actionHandler,
  reportDate,
  firstColumn,
  viewMore,
  isSearchGrid,
  isSearchQueries,
  filters,
  tabName,
  tableName,
}) => {
  const [showListItems, setShowListItems] = useState(false);
  const columnCount = columns.length;

  const dispatch = useDispatch();

  const handleViewMore = () => {
    viewMore?.();
    setShowListItems(true);
  };

  const { download } = useSelector((state: any) => ({
    download: _.get(state, "reports.download", {}),
  }));

  const [activeDownloadConfig, setActiveDownloadConfig] = useState<
    typeof downloadConfig | null
  >(null);

  const fetchState = {
    loading: download.loading,
    succeeded: download.succeeded,
    error: download.error,
  };

  const handleCancelClick = () => {
    setShowListItems(false);
  };

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: "day",
    };
  }, [reportDate,filters]);

  const handleDownloadClick = () => {
    if (!downloadConfig || !baseFilters) return;
    const request = baseFilters;

    dispatch(
      intializeDownloadReport({
        id: downloadConfig.name,
        request,
        page: 0,
        limit: 5,
      }),
    );
    setActiveDownloadConfig(downloadConfig);
  };

  const invalidateDownloadData = () => {
    setActiveDownloadConfig(null);
    dispatch(invalidateReportsDownloadData())
  };

  const isDownloadDisabled = (
    items: any[],
    downloadConfigName: string,
    activeDownloadConfigName?: string
  ): boolean => 
    Boolean(items.length && downloadConfigName !== activeDownloadConfigName);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.report}>
          <div className={styles.content}>
          <div className={styles.textContainer}>
            <div className={styles.title}>{title}</div>
            <Tooltip placement="top" content={description}>
              <i className={cx(styles.paperfliteInfo, "paperflite-info")}></i>
            </Tooltip>
          </div>
         {  tabName && tableName && <EntitiesFilterTooltip tabName={tabName} tableName={tableName} /> }
          </div>
          <div className={styles.body}>
          {loading && _.size(items) === 0 && (
            <div className={styles.loading}>Loading…</div>
          )}
            {items.length === 0 && !loading && (
              <div className={styles.noData}>
                No data for the given filters.
              </div>
            )}
          </div>
          <div className={styles.wrapper}>
            <div className={styles.list}>
              {items.slice(0, 5).map((item) => renderItem({ item,columnCount }))}
            </div>

            <div className={styles.actionWrapper}>
              {items.length > 0 && !canDownload && (
                <>
                  <div
                    className={styles.actionButton}
                    onClick={() => handleViewMore()}
                  >
                    <i className="paperflite-right-arrow"></i>
                    view more
                  </div>
                </>
              )}
              <div className={styles.actionContainer}>
                {userRoleService.canDownloadReport() && items.length > 0 && canDownload && downloadConfig && (
                  <DownloadReports
                    title={downloadConfig.title}
                    fileName={downloadConfig.fileName}
                    schema={downloadConfig.schema}
                    data={download.items || []}
                    fetchState={fetchState}
                    fetchDataForPage={(pageNumber) => {
                      dispatch(
                        intializeDownloadReport({
                          id: _.get(activeDownloadConfig,"name",""),
                          request: baseFilters,
                          page: pageNumber,
                          limit: 50,
                        }),
                      );
                    }}
                    pagination={download.pagination || {}}
                    invalidateData={invalidateDownloadData}
                    downloadClick={handleDownloadClick}
                    disabled={isDownloadDisabled(download.items, downloadConfig.name, activeDownloadConfig?.name)}
                  />
                )}
              </div>
            </div>
          </div>

          {showListItems && (
            <div>
              <ReportInsightsListView
                show={showListItems}
                title={title}
                description={description}
                items={items}
                loading={loading}
                pagination={pagination}
                loadMore={loadMore}
                onClose={handleCancelClick}
                renderItem={renderListItem}
                columns={columns}
                canDownload={canDownload}
                actionHandler={actionHandler}
                downloadConfig={downloadConfig}
                fetchState={fetchState}
                baseFilters={baseFilters}
                downloadItems={download.items}
                downloadPagination={download.pagination}
                invalidateData={invalidateDownloadData}
                firstColumn={firstColumn}
                isSearchGrid={isSearchGrid}
                isSearchQueries={isSearchQueries}
                filters={filters}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportListView;

interface ReportInsightsListViewProps {
  title: string;
  description?: string;
  items: any[];
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  onClose: () => void;
  renderItem: any;
  columns?: string[];
  show: boolean;
  canDownload?: boolean;
  actionHandler?: () => void;
  downloadConfig?: DownloadConfig;
  fetchState: any;
  baseFilters?: any;
  downloadItems: any[];
  downloadPagination: any;
  invalidateData: () => void;
  firstColumn?: string;
  isSearchGrid?: boolean;
  isSearchQueries?: boolean;
  filters?: any;
}

interface DownloadConfig {
  name: string;
  schema: any[];
  fileName: string;
  title: string;
  reportType: string;
}

const ReportInsightsListView: React.FC<ReportInsightsListViewProps> = ({
  title,
  description,
  items,
  pagination,
  loadMore,
  onClose,
  loading,
  renderItem,
  columns,
  show,
  downloadConfig,
  fetchState,
  baseFilters,
  downloadItems,
  downloadPagination,
  invalidateData,
  firstColumn,
  isSearchGrid,
  isSearchQueries,
  filters
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const dispatch = useDispatch();

  const handleCloseClick = () => {
    setIsClosing(true);
  };

  const handleAnimationEnd = () => {
    if (isClosing) {
      onClose();
    }
  };

  const [activeDownloadConfig, setActiveDownloadConfig] = useState<
    typeof downloadConfig | null
  >(null);

  const handleDownloadClick = () => {
    if (!downloadConfig || !baseFilters) return;
    const request = baseFilters;

    dispatch(
      intializeDownloadReport({
        id: downloadConfig.name,
        request,
        page: 0,
        limit: 50,
      }),
    );
    setActiveDownloadConfig(downloadConfig);
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      backdrop={true}
      scroll={false}
      className={cx(styles.customOffcanvas)}
    >
      <div
        className={cx(styles.scrollList, { [styles.slideOut]: isClosing })}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            {title}
            <Tooltip placement="right" content={description}>
              <i className={cx(styles.paperfliteInfo, "paperflite-info")} />
            </Tooltip>
          </div>
          <div className={styles.downloadContainer}>
            <div className={styles.downloadText}>
              {userRoleService.canDownloadReport() && downloadConfig && (
                <DownloadReports
                  title={downloadConfig.title}
                  fileName={downloadConfig.fileName}
                  schema={downloadConfig.schema}
                  data={downloadItems || []}
                  fetchState={fetchState}
                  fetchDataForPage={(pageNumber) => {
                    dispatch(
                      intializeDownloadReport({
                        id: _.get(activeDownloadConfig,"name",""),
                        request: baseFilters,
                        page: pageNumber,
                        limit: 50,
                      }),
                    );
                  }}
                  pagination={downloadPagination || {}}
                  invalidateData={invalidateData}
                  downloadClick={handleDownloadClick}
                />
              )}
            </div>
            <div className={styles.closeContainer} onClick={handleCloseClick}>
              <i className="paperflite-close" />
            </div>
          </div>
        </div>

        <div className={styles.tableList}>
          {isSearchGrid && (
            <div
              className={cx(styles.tableHeader, styles.headerGrid)}
              style={{ "--col-count": columns?.length } as React.CSSProperties}
            >
              {[...Array(2)].map((_, index) => (
                <div key={index}className={cx(
                  styles.headerGroup,
                  isSearchQueries && styles.headerGroupSearchQuery
                )}>
                  <div className={styles.tableTitle}>{firstColumn}</div>
                  {columns?.map((column, columnIndex) => (
                    <div key={`${index}-${columnIndex}`} className={styles.tableHeading}>
                      {column}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {!isSearchGrid && (
            <div className={styles.tableHeader}>
              <div className={styles.tableTitle}>{firstColumn}</div>
              <div className={styles.tableHead}>
                {columns?.map((column, index) => (
                  <div key={index} className={styles.tableHeading}>
                    {column}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={styles.scrollBody}>
            <InfiniteScroll
              className={cx(
                styles.scroll,
                isSearchGrid && styles.listGrid
              )}
              loading={loading}
              hasMore={pagination.currentPage + 1 < pagination.totalPages}
              loadMore={loadMore}
              loader={() => (
                <div className={styles.loader}>loading more items...</div>
              )}
            >
              {items.map((item) => renderItem({ item }))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </Offcanvas>
  );
};